import type { IJobApplicationStore } from '../index';
import {
  PER_PAGE,
  PER_PAGE_CACHE_THRESHOLD,
} from '../../../constants/numPerPage';
import JobApplication, { type IJobApplication } from '../JobApplication';
import { logError } from '../../../utils/logError';
import type { Prospect } from '../../../graphql/types';
import { mapApiResponseToStore } from './mapApiResponseToStore';
import jobApplicationService from '../../../services/jobApplicationService';

const hasMoreCache = (jobApplicationStore: IJobApplicationStore): boolean => {
  const offset = (jobApplicationStore.currentPage - 1) * PER_PAGE;

  return (
    jobApplicationStore.jobApplications.length + offset <
    jobApplicationStore.currentTotalOfJobApplications
  );
};

const cacheLowerThanThreshold = (
  jobApplicationStore: IJobApplicationStore,
): boolean =>
  jobApplicationStore.notRemovedJobApplications.length <
  PER_PAGE_CACHE_THRESHOLD;

/**
 * handleTopUpCache
 *
 * @description Function to check to see if we have enough backup Job Application on the next page or not.
 * If not, load more. This is needed because we need to backfill the current page with Job Applications if their status change.
 */
export const handleTopUpCache = async (
  jobApplicationStore: IJobApplicationStore,
): Promise<void> => {
  if (
    jobApplicationStore.cachePending ||
    !hasMoreCache(jobApplicationStore) ||
    !cacheLowerThanThreshold(jobApplicationStore)
  ) {
    return;
  }
  const pageNumber = jobApplicationStore.currentPage + 1;

  const { jobId, searchText, sortBy, bucket, filtersStore } =
    jobApplicationStore;
  try {
    const filters = {
      rrGlobalFilters: filtersStore.selectedRrFilters,
    };
    const cachedResult = await jobApplicationService.fetch(
      jobId,
      searchText,
      bucket as any,
      sortBy,
      pageNumber,
      filters,
    );

    const offset = (pageNumber - 1) * PER_PAGE;
    const jobApplications = cachedResult.nodes.map(
      (jobApplication: Prospect, index: number) =>
        JobApplication.create({
          ...mapApiResponseToStore(jobApplication),
          positionInList: offset + index + 1,
          selected: jobApplicationStore.selectedJobApplicationIds.includes(
            jobApplication.id,
          ),
        }),
    );

    // We must put all fetched load applications into loadedJobApplications to refer in other arrays
    jobApplications.forEach((newJobApp) => {
      jobApplicationStore.putToLoadedJobApplications(newJobApp);
    });

    const existingJobApplicationIds = jobApplicationStore.jobApplications.map(
      (jobApplication: IJobApplication) => jobApplication.id,
    );
    const newJobApplications = jobApplications.filter(
      (jobApplication: IJobApplication) =>
        !existingJobApplicationIds.includes(jobApplication.id),
    );
    jobApplicationStore.appendJobApplications(newJobApplications);
    jobApplicationStore.updateBucketStats(cachedResult.bucketCounts ?? {});

    jobApplicationStore.setCachedPending(false);
  } catch (error: any) {
    jobApplicationStore.setCachedPending(false);
    logError(
      {
        errorCode: 'GET_CACHED_JOB_APPLICATIONS',
        description: 'Error attempting: jobApplicationService.fetch',
      },
      { error, jobId, searchText, sortBy, bucket, filtersStore, pageNumber },
    );
  }
};
